/**
 * @file PlatformLayout.tsx
 * @description Layout кабинета платформы (phase08.5 shadcn; phase19.29 —
 * тема Commora Console).
 *
 * Тема включается хуком `useCabinetTheme` на время жизни раздела: тёмная
 * боковая панель, логотип Commora с подписью «Платформа», заголовок и
 * значок вкладки. Пункты меню — реестр `PLATFORM_NAV`: новый раздел =
 * запись (маршрут, значок, подпись, право), а не новый блок разметки.
 * На `< md` та же тёмная панель открывается шторкой (AppShell).
 *
 * Второй проход: в верхней панели — места под заголовок страницы и её
 * главное действие (`cabinet-header-slots`); страницы заполняют их через
 * `CabinetPageHeader`. На телефоне главное действие закреплено внизу —
 * обёртка содержимого резервирует под полосу отступ (по маркеру
 * `data-cabinet-bottom-action`), чтобы последний блок не прятался под ней.
 * Блок пользователя (имя, роль, смена кабинета, «Выйти») — внизу боковой
 * панели (`SidebarUserMenu`), в верхней панели его нет.
 *
 * phase19.31: палитра «Commora» — из пакета `@chaiberi/shared` (SC10);
 * полоса «вход под сотрудником» закреплена над шапкой (`AppShell banner`,
 * SC24) и не уезжает при прокрутке.
 */

import {
  Cookie,
  DatabaseBackup,
  LayoutDashboard,
  Settings2,
  ShieldUser,
  Store,
  type LucideIcon,
} from "lucide-react";
import { Outlet } from "react-router";

import { AppHeader } from "../components/AppHeader";
import { CommoraLogo } from "../components/brand/CommoraLogo";
import { ImpersonationBanner } from "../components/ImpersonationBanner";
import { SidebarUserMenu } from "../components/SidebarUserMenu";
import { RequirePermission } from "../components/guards/RequirePermission";
import { AppShell } from "../components/ui/AppShell";
import { SidebarNavLink } from "../components/ui/NavLink";
import {
  CabinetHeaderSlotsContext,
  useCabinetHeaderSlotState,
} from "../components/ui/cabinet-header-slots";
import { useAuth } from "../auth/hooks";
import { useCabinetTheme } from "../hooks/useCabinetTheme";
import { CONSENT_TEXTS_PERMISSION } from "../lib/api-consent-texts";
import { SHOPS_PERMISSION } from "../lib/api-platform-shops";

interface PlatformNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Право на пункт; нет — пункт виден всем в кабинете платформы. */
  permission?: string;
  end?: boolean;
}

/** Меню кабинета платформы — те же маршруты и права, что и раньше. */
const PLATFORM_NAV: PlatformNavItem[] = [
  { to: "/platform", label: "Обзор", icon: LayoutDashboard, end: true },
  {
    // phase19.28 этап А: магазины платформы, их адреса и интеграции (uiux-platform-shops §1).
    to: "/platform/shops",
    label: "Магазины",
    icon: Store,
    permission: SHOPS_PERMISSION,
  },
  {
    // phase19.28: настройки конкретного магазина — в его карточке, здесь общие.
    to: "/platform/integrations",
    label: "Общие интеграции",
    icon: Settings2,
    permission: "PLATFORM_MANAGE_INTEGRATIONS",
  },
  {
    to: "/platform/users",
    label: "Пользователи",
    icon: ShieldUser,
    permission: "PLATFORM_MANAGE_USERS",
  },
  {
    // phase19.16 Р3г: редакции текстов баннера cookie (uiux-r3g-consent-texts §1).
    to: "/platform/consent-texts",
    label: "Согласие на cookie",
    icon: Cookie,
    permission: CONSENT_TEXTS_PERMISSION,
  },
  {
    to: "/platform/backups",
    label: "Бэкапы",
    icon: DatabaseBackup,
    permission: "PLATFORM_MANAGE_BACKUPS",
  },
];

export function PlatformLayout() {
  // Заголовок вкладки ставит страница («Обзор — Commora»), каркас — только тему.
  useCabinetTheme("commora");
  const slots = useCabinetHeaderSlotState("Commora");
  const impersonating = !!useAuth().me?.impersonation;

  return (
    <CabinetHeaderSlotsContext.Provider value={slots.value}>
      {/* Платформа: панель закреплена с 1024 (на планшете — шторка), шапка
          под двухстрочный заголовок. Кабинет магазина — с параметрами по умолчанию. */}
      <AppShell pinSidebarAt="lg" headerSize="tall" banner={impersonating ? <ImpersonationBanner /> : undefined}>
        <AppShell.Sidebar>
          <AppShell.SidebarHeader>
            <CommoraLogo tone="light" caption="Платформа" />
          </AppShell.SidebarHeader>

          <AppShell.SidebarNav>
            <div className="flex flex-col gap-1">
              {PLATFORM_NAV.map((item) => {
                const Icon = item.icon;
                const link = (
                  <SidebarNavLink
                    key={item.to}
                    to={item.to}
                    icon={<Icon />}
                    label={item.label}
                    end={item.end}
                  />
                );
                return item.permission ? (
                  <RequirePermission key={item.to} permissions={[item.permission]} fallback="hide">
                    {link}
                  </RequirePermission>
                ) : (
                  link
                );
              })}
            </div>
          </AppShell.SidebarNav>

          {/* Блок пользователя — внизу панели, всегда виден; меню прокручивается. */}
          <AppShell.SidebarFooter flush>
            <SidebarUserMenu />
          </AppShell.SidebarFooter>
        </AppShell.Sidebar>

        {/* Шапка выровнена по колонке содержимого: название над левым краем страницы. */}
        <AppShell.Header aligned>
          <AppHeader
            start={<div ref={slots.setStartEl} className="min-w-0 flex-1" />}
            end={<div ref={slots.setEndEl} className="flex items-center" />}
            hideUser
          />
        </AppShell.Header>

        <AppShell.Content>
          {/* Отступ под закреплённую кнопку телефона — только когда она есть. */}
          <div className="max-md:has-[[data-cabinet-bottom-action]]:pb-[calc(5rem+env(safe-area-inset-bottom))]">
            <Outlet />
            {/* Место закреплённой кнопки телефона — после содержимого (порядок фокуса). */}
            <div ref={slots.setBottomEl} />
          </div>
        </AppShell.Content>
      </AppShell>
    </CabinetHeaderSlotsContext.Provider>
  );
}
